import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, interval, of, timer } from 'rxjs';
import { TableService } from '@fry/lib/list';
import { AsyncTaskOutputSummary, AsyncTaskStates, AsyncTasksStore } from '@fry/async-tasks/lib';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EasTable } from '@fry/lib/store';

export enum ExportState {
  Queued = 'queued',
  Ready = 'ready',
  Aborted = 'aborted',
  InProgress = 'inProgress',
  Completed = 'completed',
  None = 'none'
}

export interface Progress {
  state: ExportState;
  value: number;
  mode: 'indeterminate'|'determinate';
};

export const EmptyProgress: Progress  = {
  state: ExportState.None,
  value: -1,
  mode: 'indeterminate'
};

export const QueuedProgress: Progress  = {
  state: ExportState.Queued,
  value: -1,
  mode: 'indeterminate'
};

const StateMapping = new Map<AsyncTaskStates, ExportState>([
  [AsyncTaskStates.SUCCESS, ExportState.Completed],
  [AsyncTaskStates.FAILURE, ExportState.Completed],
  [AsyncTaskStates.PROCESSING, ExportState.InProgress],
  [AsyncTaskStates.PROGRESS, ExportState.InProgress],
  [AsyncTaskStates.NEW, ExportState.Queued],
  [AsyncTaskStates.ABORTED, ExportState.Aborted],
])


@Injectable()
export class TableExportsService {
  private interval = 1000;
  private stopFetching$ = new Subject<void>();

  private _exportState = new BehaviorSubject<ExportState>(ExportState.Ready);
  exportState$ = this._exportState.asObservable();

  private tracker$: Subject<void> | null = null;

  constructor(
    private list: TableService,
    private tasks: AsyncTasksStore,
  ) {
  }

  private summaryToProgress(summary: AsyncTaskOutputSummary): Progress {
    let rate = -1;
    if (summary.meta?.rate_complete !== undefined) {
      rate = summary.meta.rate_complete;
    }
    const state = StateMapping.get(summary.state) || ExportState.Completed;
    console.log(state, summary.state)
    return {
       state: state,
       value: rate,
       mode: state === ExportState.InProgress || state === ExportState.Completed ? 'determinate' : 'indeterminate'
    }
  }

  getExportProgress(taskId: string): Observable<Progress> {
    if (this.tracker$ !== null) {
      throw new Error('There already is a tracker in progress');
    }

    this.tracker$ = new Subject<void>;

    return timer(0, 2000).pipe(
      takeUntil(this.tracker$),
      switchMap(_ => {
        return this.tasks.summary(taskId);
      }),
      map(summary => {
        const progress = this.summaryToProgress(summary);
        if (progress.state === ExportState.Completed) {
          if (this.tracker$ !== null) {
            this.tracker$.next();
            this.tracker$.complete();
            this.tracker$ = null;
          }
        }
        console.log(progress);
        return progress;
      })
    )
  }

  stopExportProgress() {
    if (this.tracker$ !== null) {
      this.tracker$.next();
      this.tracker$.complete();
      this.tracker$ = null;
    }
  }

  // tableOptions should use a common interface the component
  startExport$(tableOptions: any) {
    this._exportState.next(ExportState.Queued);
    return this.list.startExport(tableOptions).pipe(
      tap(() => {
        this._exportState.next(ExportState.InProgress);
      })
    );
  }

  fetchProgress$(): Observable<number> {
    return interval(this.interval).pipe(
      takeUntil(this.stopFetching$),
      switchMap(() => this.getProgress$()),
      tap(progress => {
        const exportState = this.getExportState(progress);
        this._exportState.next(exportState);
        if (progress < 0 || progress >= 100) {
          this.stopFetching();
        }
      })
    );
  }

  private getProgress$(): Observable<number> {
    return this.fetchTaskTable(1).pipe(
      map(tableData => {
        const firstRow = tableData.data[0]?.row;
        let progressValue;
        if (!tableData.data.length || !firstRow) {
          // If there are no tasks, we are ready for a new export.
          progressValue = -1;
        }

        switch (firstRow.state) {
          case 'progress':
            progressValue = typeof firstRow.progress === 'number' ? firstRow.progress : -1;
            break
          case 'success':
            // If the export was in progress and now it's completed, we need to update the state.
            // but if the export was not yet started, we don't need to update the state.
            if (this._exportState.getValue() === ExportState.InProgress) {
              progressValue = 100;
            } else {
              progressValue = -1;
            }
            break;
          default:
            progressValue = -1;
        }
        return progressValue;
      }),
      catchError(error => {
        console.error('Error fetching progress:', error);
        return of(-1); // Return fallback value in case of errors
      })
    );
  }

  stopFetching() {
    this.stopFetching$.next();
  }

  private getExportState(progress: number) {
    if (progress < 0) {
      return ExportState.Ready;
    } else if (progress >= 0 && progress < 100) {
      return ExportState.InProgress;
    } else {
      return ExportState.Completed;
    }
  }

  public download(taskId: string) {
    return this.tasks.download(taskId);
  }

  public downloadLastExport() {
    return this.fetchTaskTable(1).subscribe({
      next: (tableData) => {
        const firstRow = tableData.data[0]?.row;
        return this.tasks.download(firstRow.id).subscribe();
      },
      error: (err) => {
        console.error('Error fetching task table:', err);
      }
    });
  }

  public fetchPreviousTaskTable$(): Observable<EasTable> {
    return this.fetchTaskTable(5).pipe(
      map(table => {
        table.columns.push({ 'id': 'download', 'label': $localize `Download`, type: 'button' });
        table.data.forEach(item => {
          if (item['row']['state'] === 'success') {
            item['row']['download'] = {
              label: $localize `Download`,
              onClick: () => {
                return this.tasks.download(item.id);
              }
            };
          }
        });

        return table;
      }),
    );
  }

  private fetchTaskTable(size: number): Observable<EasTable> {
    var payload = {filter: {type: 'export', 'metadata:listId': this.list.listId}, pagination: {size: size}}
    return this.tasks.table(payload);
  }
}
